import React, { useMemo } from "react";
import { DataTable as Table, Badge, Button, Avatar, TableStyles } from "@/components/ui";
import { Phone, UserX, UserPlus, Eye, Edit2, Clock } from "lucide-react";
import { TableActions } from "../../../components/ui/TableActions";
import toast from "react-hot-toast";
import {
  ADMIN_TABLE_HEADERS,
  TELECALLER_TABLE_HEADERS
} from "@/constants/globalConstants";
import { getMemberName, formatDate } from "../utils/customerTableHelpers";
import { TABLE_BACKGROUNDS } from "@/constants/tableBackgrounds";

/**
 * CustomersTableView
 * Main table component for displaying customers.
 */


/* Developer : Nithin
Date : 24-04-2026
changes: Renamed the table headings */

export const CustomersTableView = ({
  filtered,
  updateStatus,
  onCustomerClick,
  stages,
  onEdit,
  onToggleDisable,
  onAddActivity,
  user,
  customHeaders = null,
  maxHeight = "max-h-[600px]",
}) => {
  const isAdmin = ["admin", "owner", "Verve-admin", "systemadmin"].includes(user?.role?.toLowerCase());

  const handleCopyPhone = (phone) => {
    if (!phone) {
      toast.error("No phone number available!");
      return;
    }
    const fallbackCopy = (text) => {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        toast.success("Number copied: " + text);
      } catch (err) {
        toast.error("Failed to copy number.");
      }
      document.body.removeChild(textArea);
    };

    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(phone).then(() => {
        toast.success("Number copied: " + phone);
      }).catch(() => {
        fallbackCopy(phone);
      });
    } else {
      fallbackCopy(phone);
    }
  };

  const getStageDisplay = (status) => {
    const stage = stages?.find(s => s.key === status);
    if (!stage) return { label: status || 'New', colorClass: 'bg-slate-100 text-slate-500 border-slate-200' };
    return {
      label: stage.label,
      colorClass: `${stage.color.replace('bg-', 'text-').replace('500', '700')} bg-white border-${stage.color.replace('bg-', '')}`
    };
  };

  const columns = useMemo(() => {
    const baseCols = [
      {
        header: "Verve ID",
        key: "customId",
        minWidth: '130px',
        render: (l) => (
          <span className={TableStyles.idCell}>
            {l.customId || "—"}
          </span>
        )

      },
      {
        header: "Customer Info",
        key: "customerName",
        render: (l) => {
          const fullName = l.customerName || `${l.firstName || ""} ${l.lastName || ""}`.trim() || "No Name";
          
          const isReminderDue = l.isReminderDue;

          return (
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="flex flex-col min-w-0">
                <span className={`${TableStyles.primaryText} flex items-center gap-2`}>
                  {fullName}
                {isReminderDue && (
                  <div className="flex items-center gap-1 mt-1 text-red-500 font-bold text-[10px] animate-pulse uppercase tracking-wider">
                    <Clock size={12} />
                    <span>Follow Up Due</span>
                  </div>
                )}</span>
                <span className={`${TableStyles.secondaryText} flex items-center gap-1`}>
                  {l.phoneNumber || l.phone || "No Phone"}
                  {l.email && <span className="mx-1 opacity-50">|</span>}
                  {l.email && l.email.toLowerCase()}
                </span>
              </div>
            </div>
          );
        }
      },
      {
        header: "Address",
        key: "location",
        render: (l) => {
          const addressParts = [
            l.addressLine1,
            l.addressLine2,
            l.city,
            l.district,
            l.state,
            l.pincode
          ].filter(Boolean);
          const fullAddress = addressParts.length > 0 ? addressParts.join(', ') : "—";

          return (
            <div className="flex flex-col">
              <span
                className={`${TableStyles.regularText} max-w-[200px] line-clamp-2`}
                title={fullAddress}
              >
                {fullAddress}
              </span>
            </div>
          );
        }
      },
      {
        header: "Reference Type",
        key: "source",
        minWidth: "100px",
        render: (l) => {
          const sourceLabel = l.referenceType || (l.source === 'Bulk' ? 'Bulk Import' : (l.source || "Manual"));
          return (
            <span className={TableStyles.badge}>
              {sourceLabel}
            </span>
          );
        }
      }
    ];

    if (isAdmin) {
      baseCols.push({
        header: "Staff Name ",
        key: "telecaller",
        minWidth: "130px",
        render: (l) => (
          l.telecaller ? (
            <div className="flex flex-col min-w-0">
              <div className="text-brand-dark text-[11px] truncate" title={getMemberName(l.telecaller)}>
                {getMemberName(l.telecaller)}
              </div>
              <div className="text-[10px] text-slate-500 whitespace-nowrap">
                {formatDate(l.updatedAt || l.assignedAt)}
              </div>
            </div>
          ) : (
            <span className="text-[11px] text-slate-400">Unassigned</span>
          )
        )
      });


      baseCols.push({
        header: "Status",
        key: "teleStatus",
        minWidth: "140px",
        render: (l) => {
          const displayStatus = l.status === "drop" ? "drop" : l.teleStatus;
          const { label, colorClass } = getStageDisplay(displayStatus);
          return (
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-medium border whitespace-nowrap ${colorClass}`}>
              <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${colorClass.includes('text-emerald') ? 'bg-emerald-500' : 'bg-slate-400'}`} />
              {label}
            </span>
          );
        }
      });

      baseCols.push({
        header: "Next Stage",
        key: "nextStage",
        render: (l) => {
          const displayStatus = l.status === "drop" ? "drop" : (l.teleStatus || l.status || "");
          const isDropped = displayStatus === "drop";
          const isAppointed = !isDropped && (l.status === "converted" || l.teleStatus === "converted" || l.isConvertedByTelecaller);
          const isFullyLocked = l.isDisabled || ["project_started", "project_start"].includes(l.status);

          let availableStages = stages?.filter(s => s.key !== 'new' && s.key !== 'assigned') || [];
          if (isAppointed) {
            availableStages = availableStages.filter(s => s.key === 'converted' || s.key === 'drop');
          }

          return (
            <div className="text-center" onClick={(e) => e.stopPropagation()}>
              <select
                value={displayStatus}
                onChange={(e) => updateStatus(l, e.target.value)}
                className="text-[10px] uppercase text-brand-dark bg-white border border-slate-200 rounded px-2 py-1 cursor-pointer hover:border-brand-blue transition-colors focus:outline-none focus:ring-1 focus:ring-brand-blue"
                disabled={isFullyLocked}
              >
                <option value="" disabled>Select Status</option>
                {availableStages.map(s => (
                  <option key={s.key} value={s.key}>{s.label}</option>
                ))}
              </select>
            </div>
          );
        }
      });

      baseCols.push({
        header: "Notes",
        key: "lastActivityNote",
        className: "min-w-[250px]",
        render: (l) => {
          let lastNote = l.lastActivityNote || l.notes || "No notes yet";
          if (lastNote && typeof lastNote === 'string' && lastNote.includes(' | Note: ')) {
            lastNote = lastNote.split(' | Note: ')[1];
          }
          return (
            <span className={TableStyles.noteText}>
              {lastNote}
            </span>
          );
        }
      });

      baseCols.push({
        header: "Created Date",
        key: "createdAt",
        minWidth: "100px",
        render: (l) => (
          <span className="text-[11px] text-slate-500 whitespace-nowrap">
            {l.createdAt ? new Date(l.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : "—"}
          </span>
        )
      });

      baseCols.push({
        header: "Updated Date",
        key: "updatedAt",
        minWidth: "100px",
        render: (l) => (
          <span className="text-[11px] text-slate-500 whitespace-nowrap">
            {l.updatedAt ? new Date(l.updatedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : "—"}
          </span>
        )
      });

      baseCols.push({
        header: "Actions",
        key: "actions",
        className: "text-center w-32",
        render: (l) => {
          const isDisabled = l.isDisabled;
          const isSiteVisit = l.teleStatus === "converted" || l.status === "converted";
          const isSalesProcessing = l.isConvertedByTelecaller && !["new", "assigned", "converted", "site_visit"].includes(l.status);
          const isLocked = isDisabled || ["project_started", "project_start"].includes(l.status) || (!isSiteVisit && isSalesProcessing);

          return (
            <div onClick={(e) => e.stopPropagation()}>
              <TableActions
                onView={() => onCustomerClick(l)}
                onEdit={() => onEdit(l)}
                onCall={() => handleCopyPhone(l.phoneNumber || l.phone)}
                onToggle={() => onToggleDisable(l._id, !!l.isDisabled)}
                isActive={!l.isDisabled}
                disabled={isLocked}
              />
            </div>
          );
        }
      });
    } else {
      // Telecaller specific columns
      baseCols.push({
        header: "Stage",
        key: "teleStatus",
        minWidth: "120px",
        render: (l) => {
          const displayStatus = l.status === "drop" ? "drop" : l.teleStatus;
          const { label, colorClass } = getStageDisplay(displayStatus);
          return (
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-medium border uppercase tracking-tighter whitespace-nowrap ${colorClass}`}>
              <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${colorClass.includes('text-emerald') ? 'bg-emerald-500' : 'bg-slate-400'}`} />
              {label}
            </span>
          );
        }
      });



      baseCols.push({
        header: "Last Note",
        key: "lastCallNote",
        className: "min-w-[250px]",
        render: (l) => {
          const activities = l.callHistory || l.activities || [];
          const lastNoteObj = activities.length > 0 ? activities[0] : null;
          let lastNote = lastNoteObj ? (lastNoteObj.note || lastNoteObj.comment || lastNoteObj.notes) : (l.notes || l.lastCallNote || "No notes yet");

          if (lastNote && typeof lastNote === 'string' && lastNote.includes(' | Note: ')) {
            lastNote = lastNote.split(' | Note: ')[1];
          }

          return (
            <span className="text-[11px] text-slate-600 block leading-normal py-1 italic">
              {lastNote}
            </span>
          );
        }
      });

      baseCols.push({
        header: "Next Stage",
        key: "nextStage",
        render: (l) => {
          const displayStatus = l.status === "drop" ? "drop" : (l.teleStatus || l.status || "");
          const isDropped = displayStatus === "drop";
          const isAppointed = !isDropped && (l.status === "converted" || l.teleStatus === "converted" || l.isConvertedByTelecaller);
          const isFullyLocked = l.isDisabled || ["project_started", "project_start"].includes(l.status);

          let availableStages = stages?.filter(s => s.key !== 'new' && s.key !== 'assigned') || [];
          if (isAppointed) {
            availableStages = availableStages.filter(s => s.key === 'converted' || s.key === 'drop');
          }

          return (
            <div className="text-center" onClick={(e) => e.stopPropagation()}>
              <select
                value={displayStatus}
                onChange={(e) => updateStatus(l, e.target.value)}
                className="text-[10px] uppercase text-brand-dark bg-white border border-slate-200 rounded px-2 py-1 cursor-pointer hover:border-brand-blue transition-colors focus:outline-none focus:ring-1 focus:ring-brand-blue"
                disabled={isFullyLocked}
              >
                <option value="" disabled>Select Status</option>
                {availableStages.map(s => (
                  <option key={s.key} value={s.key}>{s.label}</option>
                ))}
              </select>
            </div>
          );
        }
      });

      baseCols.push({
        header: "Created Date",
        key: "createdAt",
        minWidth: "100px",
        render: (l) => (
          <span className="text-[11px] text-slate-500 whitespace-nowrap">
            {l.createdAt ? new Date(l.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : "—"}
          </span>
        )
      });

      baseCols.push({
        header: "Updated Date",
        key: "updatedAt",
        minWidth: "100px",
        render: (l) => (
          <span className="text-[11px] text-slate-500 whitespace-nowrap">
            {l.updatedAt ? new Date(l.updatedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : "—"}
          </span>
        )
      });

      baseCols.push({
        header: "Actions",
        key: "actions",
        className: "text-center w-32",
        render: (l) => {
          const isDisabled = l.isDisabled;
          const isSiteVisit = l.teleStatus === "converted" || l.status === "converted";
          const isSalesProcessing = l.isConvertedByTelecaller && !["new", "assigned", "converted", "site_visit"].includes(l.status);
          const isLocked = isDisabled || ["project_started", "project_start"].includes(l.status) || (!isSiteVisit && isSalesProcessing);
          /*developer: Nithin
              date : 2024-06-19
              changes : disable works for staff*/
          return (
            <div onClick={(e) => e.stopPropagation()}>
              <TableActions
                onView={() => onCustomerClick(l)}
                onEdit={() => onEdit(l)}
                onCall={() => handleCopyPhone(l.phoneNumber || l.phone)}
                onToggle={() => onToggleDisable(l._id, !!l.isDisabled)}
                isActive={!l.isDisabled}
                disabled={isLocked}
              />
            </div>
          );
        }
      });
    }

    return baseCols;
  }, [isAdmin, onAddActivity, onCustomerClick, onEdit, onToggleDisable, stages, updateStatus]);

  return (
    <Table
      columns={columns}
      data={filtered}
      backgroundImage={TABLE_BACKGROUNDS.contacts}
      /* onRowClick={(idx, row) => onCustomerClick(row)} */
      isRowInactive={(row) => !!row?.isDisabled}
      getRowClassName={(row) => {
        if (row?.isDisabled) return 'bg-slate-100/90 border-y border-slate-200/80';
        const isReminderDue = row.isReminderDue;
        return isReminderDue ? 'bg-red-50/50 hover:bg-red-50 transition-colors duration-500 animate-[pulse_3s_ease-in-out_infinite]' : 'hover:bg-slate-50/50';
      }}
      getRowTitle={(row) => {
        return row?.isReminderDue ? "Follow-Up Reminder Due" : undefined;
      }}
      maxHeight={maxHeight}
      tableClassName="min-w-full"
    />
  );
};
